import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';
import type { PlayNotifyPayload } from 'types/interfaces/play';
import './PlayNotifyToast.css';

/**
 * The toast container and SocketProvider sit outside the router, so the toast
 * cannot call useNavigate. It raises a window event instead, and
 * PlayNotifyNavigator (mounted inside the router) performs an in-app
 * navigation, never a full reload (Decision 25 link rule applies site-wide).
 */
export const PLAY_NAVIGATE_EVENT = 'trademagic:play-navigate';

export const playNotifyTarget = (p: PlayNotifyPayload): string => {
  if (!p.inviteCode) return '/play';
  if (p.event === 'JUDGE_CALL') return `/play/${p.inviteCode}/host?tab=round&filter=issues`;
  return `/play/${p.inviteCode}`;
};

export const PlayNotifyNavigator: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const onNavigate = (e: Event) => {
      const to = (e as CustomEvent<string>).detail;
      if (typeof to === 'string' && to.startsWith('/play')) navigate(to);
    };
    window.addEventListener(PLAY_NAVIGATE_EVENT, onNavigate);
    return () => window.removeEventListener(PLAY_NAVIGATE_EVENT, onNavigate);
  }, [navigate]);
  return null;
};

const Content: React.FC<{ payload: PlayNotifyPayload; toastId: string }> = ({ payload, toastId }) => (
  <button
    type="button"
    className="play-notify-toast"
    onClick={() => {
      toast.dismiss(toastId);
      window.dispatchEvent(new CustomEvent<string>(PLAY_NAVIGATE_EVENT, { detail: playNotifyTarget(payload) }));
    }}
    data-testid="play-notify-toast"
  >
    <span className="play-notify-toast__badge">PLAY</span>
    <span className="play-notify-toast__text">
      <span className="play-notify-toast__title">{payload.title}</span>
      <span className="play-notify-toast__body">{payload.body}</span>
    </span>
  </button>
);

export const showPlayNotifyToast = (payload: PlayNotifyPayload): void => {
  const toastId = `play-${payload.event}-${payload.tournamentId}-${payload.roundId ?? ''}-${payload.matchId ?? ''}`;
  toast(<Content payload={payload} toastId={toastId} />, {
    toastId,
    autoClose: payload.event === 'ROUND_START' ? 15000 : 10000,
    className: 'play-notify-toast-container',
    closeOnClick: false,
    closeButton: true,
    pauseOnHover: true,
  });
};
